import React, { useState } from "react";
import { useUserApi } from "../../services/UserApi";
import { useAppContext } from "../../contexts/AppContext";
import UserTable from "./UserTable";
import UserFilters from "./UserFilters";
import Heading from "carbon-react/lib/components/heading";
import ErrorMessage from "../messages/ErrorMessage";
import CommentsDialog from "../common/CommentsDialog";
import ChangeEmailDialog from "./changeEmail/ChangeEmailDialog";
import VerifyEmailAccessDialog from "./verifyEmailAccess/VerifyEmailAccessDialog";
import ResetPasswordDialog from "./resetPassword/ResetPasswordDialog";
import DeleteUserDialog from "./deleteUser/DeleteUserDialog";
import BypassEmailVerificationDialog from "./bypassEmailVerification/BypassEmailVerificationDialog";
import BypassMfaDialog from "./bypassMfa/BypassMfaDialog";

import "./Users.css";

const Users = () => {
  const [loading, setLoading] = useState(false);
  const [fetchUsersFailed, setFetchUsersFailed] = useState(false);
  const [commentsDialogState, setCommentsDialogState] = useState(null);
  const [commentsDialogOpen, setCommentsDialogOpen] = useState(false);
  const [changeEmailDialogOpen, setChangeEmailDialogOpen] = useState(false);
  const [changeEmailDialogUser, setChangeEmailDialogUser] = useState(null);
  const [deleteUserDialogOpen, setDeleteUserDialogOpen] = useState(false);
  const [deleteUserDialogUser, setDeleteUserDialogUser] = useState(null);
  const [
    bypassEmailVerificationDialogOpen,
    setBypassEmailVerificationDialogOpen,
  ] = useState(false);
  const [
    bypassEmailVerificationDialogUser,
    setBypassEmailVerificationDialogUser,
  ] = useState(null);
  const [
    bypassMfaDialogUser,
    setBypassMfaDialogUser,
  ] = useState(null);
  const [
    bypassMfaDialogOpen,
    setBypassMfaDialogOpen,
  ] = useState(null);
  const [
    verifyEmailAccessDialogOpen,
    setVerifyEmailAccessDialogOpen,
  ] = useState(false);
  const [
    verifyEmailAccessDialogUser,
    setVerifyEmailAccessDialogUser,
  ] = useState(null);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [resetPasswordDialogUser, setResetPasswordDialogUser] = useState(null);
  const { fetchUsers } = useUserApi();
  const { bypassMfa } = useUserApi();
  const {
    userResultSet,
    setUserResultSet,
    userFilters,
    setUserFilters,
  } = useAppContext();

  const getUsers = async (
    skip = 0,
    pageSize = userResultSet?.pageSize || 15
  ) => {
    try {
      setLoading(true);
      setFetchUsersFailed(false);

      const apiResult = await fetchUsers(
        userFilters.searchBy,
        userFilters.searchValue,
        skip,
        pageSize
      );

      if (!apiResult?.success || !apiResult?.data)
        throw new Error(apiResult?.error?.description || "Unexpected response");

      const resultSet = {
        ...apiResult.data,
        currentPage:
          apiResult.data.start > 0 ? apiResult.data.start / pageSize + 1 : 1,
        lastPage: apiResult.data.length < pageSize,
        pageSize,
      };
      setUserResultSet({ ...resultSet });
    } catch (error) {
      setFetchUsersFailed(true);
    }
    setLoading(false);
  };

  const onUpdateUser = (updatedUser) => {
    const index = userResultSet.users.findIndex(
      (user) => user.userId === updatedUser.userId
    );
    if (index < 0) return;

    userResultSet.users[index] = { ...updatedUser };
    setUserResultSet({ ...userResultSet });
  };

  const refreshUsersForCurrentPage = async () => {
    const skip =
      userResultSet?.currentPage && userResultSet.currentPage > 1
        ? (userResultSet.currentPage - 1) * userResultSet.pageSize
        : 0;
    await getUsers(skip);
  };

  const onPagePrev = async () => {
    const skip =
      userResultSet?.currentPage && userResultSet.currentPage > 1
        ? (userResultSet.currentPage - 2) * userResultSet.pageSize
        : 0;
    await getUsers(skip);
  };

  const onPageNext = async () => {
    if (userResultSet?.lastPage) {
      return;
    }
    const skip = userResultSet?.currentPage
      ? userResultSet.currentPage * userResultSet.pageSize
      : 0;
    await getUsers(skip);
  };

  const onSearchUsers = async () => {
    await getUsers();
  };

  const openCommentsDialog = (dialogState) => {
    setCommentsDialogState(dialogState);
    setCommentsDialogOpen(true);
  };

  const onCommentsDialogClose = () => {
    setCommentsDialogState(null);
    setCommentsDialogOpen(false);
  };

  const openChangeEmailDialog = (user) => {
    setChangeEmailDialogUser(user);
    if (user) {
      setChangeEmailDialogOpen(true);
    }
  };

  const closeChangeEmailDialog = () => {
    setChangeEmailDialogUser(null);
    setChangeEmailDialogOpen(false);
  };

  const openVerifyEmailAccessDialog = (user) => {
    setVerifyEmailAccessDialogUser(user);
    if (user) {
      setVerifyEmailAccessDialogOpen(true);
    }
  };

  const closeVerifyEmailAccessDialog = () => {
    setVerifyEmailAccessDialogUser(null);
    setVerifyEmailAccessDialogOpen(false);
  };

  const openResetPasswordDialog = (user) => {
    if (!user) return;
    setResetPasswordDialogUser(user);
    setResetPasswordDialogOpen(true);
  };

  const closeResetPasswordDialog = () => {
    setResetPasswordDialogUser(null);
    setResetPasswordDialogOpen(false);
  };

  const openDeleteUserDialog = (user) => {
    if (!user) return;
    setDeleteUserDialogUser(user);
    setDeleteUserDialogOpen(true);
  };

  const closeDeleteUserDialog = (wasDeleted = false) => {
    setDeleteUserDialogUser(null);
    setDeleteUserDialogOpen(false);
    if (wasDeleted) refreshUsersForCurrentPage();
  };

  const openBypassEmailVerificationDialog = (user) => {
    if (!user) return;
    setBypassEmailVerificationDialogUser(user);
    setBypassEmailVerificationDialogOpen(true);
  };

  const closeBypassEmailVerificationDialog = () => {
    setBypassEmailVerificationDialogUser(null);
    setBypassEmailVerificationDialogOpen(false);
  };

  const openBypassMfaDialog = (user) => {
    if (!user) return;
    setBypassMfaDialogUser(user);
    setBypassMfaDialogOpen(true);
  };

  const closeBypassMfaDialog = () => {
    setBypassMfaDialogUser(null);
    setBypassMfaDialogOpen(false);
  };

  return (
    <>
      <Heading
        title="Manage users"
        divider={false}
        className="manageUsersHeading"
      />
      <UserFilters
        onUpdateUserFilters={setUserFilters}
        userFilters={userFilters}
        onSearch={onSearchUsers}
      />
      {fetchUsersFailed === true ? (
        <ErrorMessage message="Failed to read user data" />
      ) : (
        <UserTable
          userResultSet={userResultSet}
          loading={loading}
          onUpdateUser={onUpdateUser}
          openCommentsDialog={openCommentsDialog}
          openChangeEmailDialog={openChangeEmailDialog}
          openVerifyEmailAccessDialog={openVerifyEmailAccessDialog}
          openResetPasswordDialog={openResetPasswordDialog}
          openDeleteUserDialog={openDeleteUserDialog}
          openBypassEmailVerificationDialog={openBypassEmailVerificationDialog}
          openBypassMfaDialog={openBypassMfaDialog}
          onPagePrev={onPagePrev}
          onPageNext={onPageNext}
        />
      )}
      <CommentsDialog
        dialogState={commentsDialogState}
        open={commentsDialogOpen}
        onClose={onCommentsDialogClose}
      />
      <ChangeEmailDialog
        open={changeEmailDialogOpen}
        onClose={closeChangeEmailDialog}
        onSave={onUpdateUser}
        user={changeEmailDialogUser}
      />
      <VerifyEmailAccessDialog
        open={verifyEmailAccessDialogOpen}
        onClose={closeVerifyEmailAccessDialog}
        user={verifyEmailAccessDialogUser}
      />
      <ResetPasswordDialog
        open={resetPasswordDialogOpen}
        onClose={closeResetPasswordDialog}
        user={resetPasswordDialogUser}
      />
      <DeleteUserDialog
        open={deleteUserDialogOpen}
        user={deleteUserDialogUser}
        onCancel={closeDeleteUserDialog}
        onDeleted={() => closeDeleteUserDialog(true)}
      />
      <BypassEmailVerificationDialog
        open={bypassEmailVerificationDialogOpen}
        user={bypassEmailVerificationDialogUser}
        onClose={closeBypassEmailVerificationDialog}
      />
      <BypassMfaDialog
        open={bypassMfaDialogOpen}
        user={bypassMfaDialogUser}
        onClose={closeBypassMfaDialog}
      />
    </>
  );
};

export default Users;
